import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import "../style.css";
import GoogleMap from 'google-maps-react-markers';
import FilterMenu from "./FilterMenu";
import ResultModel from "./ResultModel";
import ResultButton from "./ResultButton";
import ResultReport from "./ResultReport";
import ResultMultimedia from "./ResultMultimedia";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Home() {
    const navigate = useNavigate();
    const location = useLocation();
    const [keyword, setKeyword] = useState(location.state?.keyword || ""); 
    const [filteredMarker, setFilteredMarker] = useState([]);
    const mapRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [resultCount, setResultCount] = useState(null);
    const [currentView, setCurrentView] = useState('model');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [modelData, setModelData] = useState([])
    const [reportData, setReportData] = useState([])
    const [mediaData, setMediaData] = useState([])
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        country: "",
        state: "",
        category: "",
        majorLithology: "",
        discipline: "",
        rockType: "",
        formation: "",
        age: ""
    });
    const [filteredModelData, setFilteredModelData] = useState([]);
    const [filteredReportData, setFilteredReportData] = useState([]);
    const [filteredMediaData, setFilteredMediaData] = useState([]);
    const [isOpenLegend, setIsOpenLegend] = useState(false)

    const onGoogleApiLoaded = ({ map, maps }) => {
        mapRef.current = map;
        setMapReady(true);
    };

    const handleReset = () => {
        setFilters({
            country: "",
            state: "",
            category: "",
            majorLithology: "",
            discipline: "",
            rockType: "",
            formation: "",
            age: ""
        });
        setFilteredModelData(modelData);
        setFilteredReportData(reportData);
        setFilteredMediaData(mediaData);
        setKeyword('');
        navigate('/home');
    };

    const handleCloseResults = () => {
        setShowResults(false);
    };

    const handleButtonClick = (view) => {
        setCurrentView(view);
    };

    useEffect(() => {
        setResultCount(filteredMarker.length);
    }, [filteredMarker]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const modelsResponse = await fetch('https://geoid-rest.vercel.app/models');
                const reportsResponse = await fetch('https://geoid-rest.vercel.app/paper_report');
                const mediasResponse = await fetch('https://geoid-rest.vercel.app/media');
                const modelRes = await modelsResponse.json()
                const reportRes = await reportsResponse.json()
                const mediaRes = await mediasResponse.json()

                const filteredModelData = modelRes.filter(model => 
                    model.data && 
                    model.data.latitude && 
                    model.data.longitude &&
                    !isNaN(parseFloat(model.data.latitude)) &&
                    !isNaN(parseFloat(model.data.longitude)) &&
                    model.access === 'public'
                );

                const filteredReportData = reportRes.filter(report => 
                    report && 
                    report.lat && 
                    report.long &&
                    !isNaN(parseFloat(report.lat)) &&
                    !isNaN(parseFloat(report.long)) &&
                    report.access === 'public'
                );

                const filteredMediaData = mediaRes.filter(media => 
                    media && 
                    media.lat && 
                    media.long &&
                    !isNaN(parseFloat(media.lat)) &&
                    !isNaN(parseFloat(media.long)) &&
                    media.access === 'public'
                );
                
                setModelData(filteredModelData)
                setReportData(filteredReportData)
                setMediaData(filteredMediaData)
                
                // Set filtered data initially
                setFilteredModelData(filteredModelData)
                setFilteredReportData(filteredReportData)
                setFilteredMediaData(filteredMediaData)
                
            } catch (error) {
                console.error('Error fetching user uploads:', error);
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, []);

    useEffect(() => {
        if (keyword) {
            const filtered = modelData.filter(artifact => {
                return (
                    artifact.name?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.location?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.title?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.author?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.country?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.state?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.district?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.city?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.type?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.discipline?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.formation?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.rockType?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.majorLithology?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.age?.toLowerCase().includes(keyword.toLowerCase()) ||
                    artifact.data?.keyword?.toLowerCase().includes(keyword.toLowerCase())
                );
            });
            setFilteredModelData(filtered);

            const filteredReports = reportData.filter(report =>
                report.title.toLowerCase().includes(keyword.toLowerCase()) ||
                report.author.toLowerCase().includes(keyword.toLowerCase()) ||
                report.category.toLowerCase().includes(keyword.toLowerCase()) ||
                report.tags.some(tag => tag.toLowerCase().includes(keyword.toLowerCase()))
            );
            setFilteredReportData(filteredReports);

            const filteredMedia = mediaData.filter(item =>
                item.name.toLowerCase().includes(keyword.toLowerCase()) ||
                item.location.toLowerCase().includes(keyword.toLowerCase()) ||
                item.category.toLowerCase().includes(keyword.toLowerCase())
            );
            setFilteredMediaData(filteredMedia);
            setShowResults(true);
        } else {
            // Reset filtered data if keyword is empty
            setFilteredModelData(modelData);
            setFilteredReportData(reportData);
            setFilteredMediaData(mediaData);
        }
    }, [keyword, modelData, reportData, mediaData]);

    const handleApplyFilter = (newFilters) => {
        setFilters(newFilters);
        setShowResults(true);

        // Filter model data
        const filteredModels = modelData.filter(model => {
            if (newFilters.state && model.data.state.toLowerCase() !== newFilters.state.toLowerCase()) return false;
            if (newFilters.category) {
                if (newFilters.category === 'geology') {
                    if (model.data.type === 'Archaeology') return false;
                } else if (newFilters.category === 'archaeology') {
                    if (model.data.type === 'Geology') return false;
                }
            }
            if (newFilters.discipline && model.data.discipline !== newFilters.discipline) return false;
            return true;
        });
        setFilteredModelData(filteredModels);

        const filteredReports = reportData.filter(report => {
            if (newFilters.state && report.state.toLowerCase() !== newFilters.state.toLowerCase()) return false;
            if (newFilters.category) {
                if (newFilters.category === 'geology') {
                    if (report.category === 'Archaeology') return false;
                } else if (newFilters.category === 'archaeology') {
                    if (report.category !== 'Archaeology') return false;
                }
            }
            return true;
        });
        setFilteredReportData(filteredReports);

        // Filter media data
        const filteredMedia = mediaData.filter(media => {
            if (newFilters.state && media.state.toLowerCase() !== newFilters.state.toLowerCase()) return false;
            if (newFilters.category) {
                if (newFilters.category === 'geology') {
                    if (media.category === 'Archaeology') return false;
                } else if (newFilters.category === 'archaeology') {
                    if (media.category !== 'Archaeology') return false;
                }
            }
            return true;
        });
        setFilteredMediaData(filteredMedia);
    };

    const ModelMarker = ({category, title, model, data, artifact}) => {
        const [isHovering, setIsHovering] = useState(false);
        let categoryLabel = 'Unknown Category'

        const handleClick = () => {
            navigate('/model/view-model', { state: { model, data, artifact } });
        };

        const backgroundColor = category
            ? category.toLowerCase() === 'archaeology'
            ? "orange"
            : "yellow"
            : "gray";

        if (category.toLowerCase() ===  'archaeology'){
            categoryLabel = 'Archaelogy'
        } else if (category ===  'gAll'){
            categoryLabel = 'Geology - All'
        } else if (category ===  'gGeneral'){
            categoryLabel = 'Geology - General'
        } else if (category ===  'gONG'){
            categoryLabel = 'Geology - Oil & Gas'
        } else if (category ===  'gMining'){
            categoryLabel = 'Geology - Mining'
        } else if (category ===  'gEngineering'){
            categoryLabel = 'Geology - Engineering/Geotechnical'
        } else if (category ===  'gEnvironment'){
            categoryLabel = 'Geology - Environment'
        }

        return (
            <div
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={handleClick}
            style={{
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: backgroundColor,
                border: '2px solid black',
                borderRadius: "50%",
                cursor: 'pointer',
                opacity: '70%'
              }}
            />
            {isHovering && (
              <div
                style={{
                  position: 'absolute',
                  top: '-40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'white',
                  padding: '5px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  zIndex: 1000,
                  whiteSpace: 'nowrap',
                }}
              >
                <div><strong>{title}</strong></div>
                <div><strong>3D Model</strong></div>
                <div>{categoryLabel}</div>
              </div>
            )}
          </div>
        );
    };
    
    const ReportMarker = ({ category, title, file, id }) => {
        const [isHovering, setIsHovering] = useState(false);
        let categoryLabel = 'Unknown Category'

        const handleClick = () => {
            window.open(`/html/${id}`, '_blank');
        };

        const backgroundColor = category
            ? category.toLowerCase() === 'archaeology'
            ? "orange"
            : "yellow"
            : "gray";

        if (category.toLowerCase() ===  'archaeology'){
            categoryLabel = 'Archaelogy'
        } else if (category ===  'gAll'){
            categoryLabel = 'Geology - All'
        } else if (category ===  'gGeneral'){
            categoryLabel = 'Geology - General'
        } else if (category ===  'gONG'){
            categoryLabel = 'Geology - Oil & Gas'
        } else if (category ===  'gMining'){
            categoryLabel = 'Geology - Mining'
        } else if (category ===  'gEngineering'){
            categoryLabel = 'Geology - Engineering/Geotechnical'
        } else if (category ===  'gEnvironment'){
            categoryLabel = 'Geology - Environment'
        }

        return (
            <div
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={handleClick}
                style={{
                    position: 'relative',
                    cursor: 'pointer',
                }}
            >
                <div
                    style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: backgroundColor,
                        border: '2px solid black',
                        opacity: '70%'
                    }}
                />
                {isHovering && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '-40px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'white',
                            padding: '5px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <div><strong>{title}</strong></div>
                        <div><strong>Report / Journal</strong></div>
                        <div>{categoryLabel}</div>
                    </div>
                )}
            </div>
        );
    };

    const MediaMarker = ({ category, title, media }) => {
        const [isHovering, setIsHovering] = useState(false);
        let categoryLabel = 'Unknown Category'

        const openMediaViewer = (mediaId) => {
            navigate(`/viewMedia/${mediaId}`);
        };

        const backgroundColor = category
            ? category.toLowerCase() === 'archaeology'
            ? "orange"
            : "yellow"
            : "gray";

        if (category.toLowerCase() ===  'archaeology'){
            categoryLabel = 'Archaelogy'
        } else if (category.toLowerCase() ===  'geology'){
            categoryLabel = 'Geology'
        } else if (category ===  'gAll'){
            categoryLabel = 'Geology - All'
        } else if (category ===  'gGeneral'){
            categoryLabel = 'Geology - General'
        } else if (category ===  'gONG'){
            categoryLabel = 'Geology - Oil & Gas'
        } else if (category ===  'gMining'){
            categoryLabel = 'Geology - Mining'
        } else if (category ===  'gEngineering'){
            categoryLabel = 'Geology - Engineering/Geotechnical'
        } else if (category ===  'gEnvironment'){
            categoryLabel = 'Geology - Environment'
        }

        return (
            <div
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={() => openMediaViewer(media._id)}
                style={{
                    position: 'relative',
                    cursor: 'pointer',
                }}
            >
                {backgroundColor === 'yellow' &&(
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="yellow" class="bi bi-triangle-fill" viewBox="0 0 16 16" opacity="0.7">
                        <path fill-rule="evenodd" d="M7.022 1.566a1.13 1.13 0 0 1 1.96 0l6.857 11.667c.457.778-.092 1.767-.98 1.767H1.144c-.889 0-1.437-.99-.98-1.767z" 
                            fill="yellow" stroke="black" stroke-width="1"/>
                    </svg>
                )}
                {backgroundColor === 'orange' &&(
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="orange" class="bi bi-triangle-fill" viewBox="0 0 16 16" opacity="0.7">
                        <path fill-rule="evenodd" d="M7.022 1.566a1.13 1.13 0 0 1 1.96 0l6.857 11.667c.457.778-.092 1.767-.98 1.767H1.144c-.889 0-1.437-.99-.98-1.767z" 
                            fill="orange" stroke="black" stroke-width="1"/>
                    </svg>
                )}
                {isHovering && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '-40px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'white',
                            padding: '5px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <div><strong>{title}</strong></div>
                        <div><strong>Multimedia</strong></div>
                        <div>{categoryLabel}</div>
                    </div>
                )}
            </div>
        );
    };

    const Marker = ({ text, path, type }) => (
        <div
            style={{
                position: "absolute",
                transform: "translate(-50%, -100%)",
                color: "black",
                textAlign: "center",
                width: "120px",
                cursor: "pointer" // Add cursor style to indicate it's clickable
            }}
        >
            <div
                style={{
                    background: type === 'geology' ? "yellow" : "orange",
                    padding: "10px",
                    borderRadius: "8px",
                    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {text}
            </div>
            <div
                style={{
                    width: 0,
                    height: 0,
                    borderLeft: "10px solid transparent",
                    borderRight: "10px solid transparent",
                    borderTop: type === 'geology' ? "10px solid yellow" : "10px solid orange",
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: "100%",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                }}
            />
        </div>
    );

    return (
        <div style={{ height: "100vh", width: "100%" }}>
            {loading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                </div>
            )}
            <GoogleMap
                defaultCenter={{ lat: 4.156156, lng: 103.502969 }}
                defaultZoom={7}
                mapMinHeight="100vh"
                onGoogleApiLoaded={onGoogleApiLoaded}
                onChange={(map) => console.log('Map moved', map)}
            >
                {filteredModelData.map((marker) => (
                    <ModelMarker
                        category={marker.data.discipline}
                        title={marker.name}
                        model={marker.model}
                        data={marker.data}
                        artifact={marker}
                        lat={parseFloat(marker.data.latitude)}
                        lng={parseFloat(marker.data.longitude)}
                    />
                ))}

                {filteredReportData.map((marker, index) => (
                    <ReportMarker
                        key={index}
                        category={marker.category}
                        title={marker.title}
                        lat={parseFloat(marker.lat)}
                        lng={parseFloat(marker.long)}
                        file={marker.file}
                        id={marker._id}
                    />
                ))}

                {filteredMediaData.map((marker, index) => (
                    <MediaMarker
                        key={index}
                        category={marker.category}
                        title={marker.name}
                        lat={parseFloat(marker.lat)}
                        lng={parseFloat(marker.long)}
                        media={marker}
                    />
                ))}
            </GoogleMap>

            {/* Floating Filter Menu Box */}
            <div style={{ overflow: 'hidden' }}>
                <FilterMenu onApplyFilter={handleApplyFilter} reset={handleReset}/>
            </div>

            <div className="legend-box">
                <span style={{display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '10px'}} onClick={() => setIsOpenLegend(!isOpenLegend)}>
                    <span>Legend</span>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-info-circle" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                        </svg>
                    </div>
                    <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#00712D" class="bi bi-caret-down-fill" viewBox="0 0 16 16">
                        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                    </svg>
                    </div>
                </span>

                {isOpenLegend &&(
                    <>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div
                            style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: "yellow",
                                border: '2px solid black',
                                borderRadius: "50%",
                                cursor: 'pointer',
                                opacity: '70%'
                            }}
                        />
                        - 3D Model (Geology)
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div
                            style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: "orange",
                                border: '2px solid black',
                                borderRadius: "50%",
                                cursor: 'pointer',
                                opacity: '70%'
                            }}
                        />
                        - 3D Model (Archaeology)
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div
                            style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: "yellow",
                                border: '2px solid black',
                                opacity: '70%'
                            }}
                        />
                        - Reports (Geology)
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div
                            style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: "orange",
                                border: '2px solid black',
                                opacity: '70%'
                            }}
                        />
                        - Reports (Archaeology)
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="yellow" class="bi bi-triangle-fill" viewBox="0 0 16 16">
                        <path fill-rule="evenodd" d="M7.022 1.566a1.13 1.13 0 0 1 1.96 0l6.857 11.667c.457.778-.092 1.767-.98 1.767H1.144c-.889 0-1.437-.99-.98-1.767z" 
                                fill="yellow" stroke="black" stroke-width="1"/>
                        </svg>
                        <span> - Multimedia (Geology)</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="orange" class="bi bi-triangle-fill" viewBox="0 0 16 16">
                        <path fill-rule="evenodd" d="M7.022 1.566a1.13 1.13 0 0 1 1.96 0l6.857 11.667c.457.778-.092 1.767-.98 1.767H1.144c-.889 0-1.437-.99-.98-1.767z" 
                                fill="orange" stroke="black" stroke-width="1"/>
                        </svg>
                        <span> - Multimedia (Archaeology)</span>
                    </div>
                    </>
                )}
            </div>

            {/* Search Results Placeholder */}
            {showResults && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "0",
                        left: "0",
                        width: "100%",
                        height: "37%",
                        background: "white",
                        boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.2)",
                        padding: "20px",
                        zIndex: 1000,
                        overflowY: "auto",
                        backgroundColor: '#F5F7FA'
                    }}
                >
                    <div>
                        <ResultButton onClose={handleCloseResults} onButtonClick={handleButtonClick} />
                        {currentView === 'model' && <ResultModel filters={filters} keyword={keyword} />}
                        {currentView === 'reports' && <ResultReport filters={filters} keyword={keyword}/>}
                        {currentView === 'multimedia' && <ResultMultimedia filters={filters} keyword={keyword}/>}
                    </div>
                </div>
            )}
            <ToastContainer />
        </div>
    );
}

export default Home;